import { useState } from 'react'

import api from '@/services/api'
import { formatWhen, readNewsSearchApproval, type NewsSearchApproval } from './newsSignOff'

function refusal(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() ? detail : 'The sign-off could not be saved.'
}

/**
 * The client's written sign-off for Contact News (CRE-20 / LPR-27 / MOD-13).
 *
 * News search sends counterparty names to Serper, and the client was told it
 * stays off until they sign off in writing. A saved key does not switch it
 * on: the server refuses every lookup until an admin records the sign-off
 * here, with a reference to it, and the change is audited.
 */
export default function NewsSignOffPanel({
  approval: value,
  onChanged,
}: {
  approval: unknown
  onChanged: (next: NewsSearchApproval) => void
}) {
  const approval = readNewsSearchApproval(value)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (approved: boolean) => {
    setBusy(true)
    setError(null)
    try {
      const res = await api.put('/settings/news-search-approval', {
        approved,
        note: approved ? note.trim() : '',
      })
      const next = readNewsSearchApproval(
        (res.data as { newsSearchApproval?: unknown } | undefined)?.newsSearchApproval,
      )
      setNote('')
      onChanged(next)
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-lg space-y-2 rounded-lg border border-white/[0.06] bg-black/20 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.12em] text-stone-500">Client sign-off</div>
      {approval.approved ? (
        <>
          <p className="text-[11px] text-emerald-300/90">
            News search is on. Sign-off recorded by {approval.approvedBy ?? 'an admin'}
            {formatWhen(approval.approvedAt) ? ` on ${formatWhen(approval.approvedAt)}` : ''}.
          </p>
          {approval.note ? <p className="text-[11px] text-stone-400">Reference: {approval.note}</p> : null}
          <button
            type="button"
            className="text-[11px] text-stone-300 underline-offset-2 hover:text-stone-100 hover:underline disabled:opacity-40"
            disabled={busy}
            onClick={() => void submit(false)}
          >
            Switch news search off
          </button>
        </>
      ) : (
        <>
          <p className="text-[11px] text-amber-200/90">
            News search is off. No counterparty name is sent to Serper — even with a key saved — until an
            admin records the client’s written sign-off.
          </p>
          <label className="block text-[10px] text-stone-500">
            Written sign-off reference (who, where, when)
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="e.g. email from the client, 30 Sep 2026"
              className="mt-1 w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/35 focus:outline-none"
            />
          </label>
          <button
            type="button"
            className="btn-primary text-[12px] disabled:opacity-50"
            disabled={busy || !note.trim()}
            onClick={() => void submit(true)}
          >
            Record sign-off and switch on
          </button>
        </>
      )}
      {error ? <p className="text-[11px] text-red-300">{error}</p> : null}
    </div>
  )
}
